import { useCallback, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useReservation } from '@updater/ui-widgets';
import { useUserData } from 'flows/core/hooks/use-profile';
import { useTracking } from 'react-tracking';
import { TrackEventInput, TrackEventVerb } from '@updater/ui-tracker';
import forEach from 'lodash/forEach';

type TrackableEvents = {
  [event: string]: {
    verb: TrackEventVerb;
    LPAppName: string;
    LPEventName: string;
  };
};

const TRACKABLE_EVENTS: TrackableEvents = {
  chat_opened: {
    verb: 'opened',
    LPAppName: 'LP_OFFERS',
    LPEventName: 'OFFER_CLICK',
  },
};

export const useChatTracking = () => {
  const router = useRouter();
  const { user } = useUserData();
  const { trackEvent } = useTracking<TrackEventInput<unknown>>({
    domain: 'home_services',
  });

  const { reservationInfo, offer } = useReservation({
    moveId: user?.currentMove?.id ?? '',
    skip: !user?.currentMove?.id,
  });

  const boundEvents = useRef<Set<string>>(new Set());
  const userRef = useRef(user);
  const reservationInfoRef = useRef(reservationInfo);
  const offerRef = useRef(offer);

  // Update refs whenever the values change to prevent stale trackChatEvent closure
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    reservationInfoRef.current = reservationInfo;
  }, [reservationInfo]);

  useEffect(() => {
    offerRef.current = offer;
  }, [offer]);

  const trackChatEvent = useCallback(
    (event: string) => {
      const currentPath = router.pathname;

      const trackingData = {
        pagePath: currentPath,
        event,
        moveId: userRef.current?.currentMove?.id,
        leadId: reservationInfoRef.current?.code,
        offerId: offerRef.current?.code?.toString(),
      };

      trackEvent({
        object: 'liveperson_chat',
        verb: TRACKABLE_EVENTS[event]?.verb,
        details: trackingData,
      });
    },
    [router.pathname]
  );

  const bindLivePersonEvents = useCallback(() => {
    if (typeof window === 'undefined') return;

    const lpTag = (window as any).lpTag;
    if (!lpTag) return;

    // Check if lpTag has the proper structure before attempting to bind
    if (!lpTag.events) {
      console.info('LivePerson events not available for binding');
      return;
    }

    // Bind each event, checking if it's already bound
    forEach(TRACKABLE_EVENTS, (config, event) => {
      const { LPAppName, LPEventName } = config;
      const eventKey = `${LPAppName}-${LPEventName}`;

      // Check if this specific event has already been bound
      if (boundEvents.current.has(eventKey)) {
        return;
      }

      lpTag.events.bind({
        appName: LPAppName,
        eventName: LPEventName,
        func: () => trackChatEvent(event),
      });

      boundEvents.current.add(eventKey);
    });
  }, [trackChatEvent]);

  return {
    trackChatEvent,
    bindLivePersonEvents,
  };
};
